import React, { useLayoutEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useResizeDetector } from 'react-resize-detector'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@mui/material/styles'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import CloudOffIcon from '@mui/icons-material/CloudOff'

import logoImg from '@/assets/img/logo.png'

import { closedMixin, openedMixin } from '@/utils/mui'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useOfflineMode } from '@/providers/OfflineModeProvider'

import { offlineSidebarItems } from './offlineSidebarItems'

export const sidebarExpandedWidth = 200
export const sidebarCollapsedWidth = 60

export default function OfflineSidebar() {
    const { pathname } = useLocation()
    const theme = useTheme()
    const { t } = useTranslation()
    const { height: menuItemsStackWrapperHeight, ref: menuItemsStackWrapperRef } = useResizeDetector()
    const menuItemsBoxWrapperRef = useRef<HTMLDivElement>(null)

    const [expanded, setExpanded] = useState<boolean>(false)
    const [menuItemSpacing, setMenuItemSpacing] = useState<number>(5)

    const authUser = useAppSelector(selectAuthUser)
    const { isOfflineMode, timeUntilExpiry } = useOfflineMode()

    useLayoutEffect(() => {
        if (menuItemsBoxWrapperRef.current && menuItemsStackWrapperHeight) {
            const menuItemsBoxWrapperHeight = menuItemsBoxWrapperRef.current.getBoundingClientRect().height
            if (menuItemsStackWrapperHeight > menuItemsBoxWrapperHeight) setMenuItemSpacing((prevMenuItemSpacing) => Math.max(prevMenuItemSpacing - 1, 0))
        }
    }, [menuItemsStackWrapperHeight])

    const toggleSidebar = () => {
        setExpanded((prevExpanded) => !prevExpanded)
    }

    const formatTimeRemaining = (hours: number) => {
        const h = Math.floor(hours)
        const m = Math.floor((hours % 1) * 60)
        return `${h}h ${m}m`
    }

    return (
        <Box sx={{ position: 'relative', zIndex: (theme) => theme.zIndex.drawer + 2 }}>
            <IconButton
                onClick={toggleSidebar}
                size="small"
                sx={[
                    {
                        backgroundColor: '#269CA6',
                        position: 'fixed',
                        top: '65px',
                        left: expanded ? sidebarExpandedWidth : sidebarCollapsedWidth,
                        transform: 'translate(-50%, -50%)',
                        zIndex: (theme) => theme.zIndex.drawer + 2,
                        '&:hover': {
                            backgroundColor: '#269CA6',
                        },
                    },
                    expanded && {
                        transition: theme.transitions.create('left', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                    },
                    !expanded && {
                        transition: theme.transitions.create('left', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                        }),
                    },
                ]}
            >
                {expanded ? <ChevronLeftRoundedIcon htmlColor="#FFF" fontSize="small" /> : <ChevronRightRoundedIcon htmlColor="#FFF" fontSize="small" />}
            </IconButton>
            <Drawer
                variant="permanent"
                sx={[
                    {
                        overflowX: 'hidden',
                        height: '100vh',
                        flexShrink: 0,
                        [`& .MuiDrawer-paper`]: {
                            borderRight: 'none',
                            overflowX: 'hidden',
                        },
                    },
                    expanded && {
                        ...openedMixin(sidebarExpandedWidth)(theme),
                        '& .MuiDrawer-paper': openedMixin(sidebarExpandedWidth)(theme),
                    },
                    !expanded && {
                        ...closedMixin(sidebarCollapsedWidth)(theme),
                        '& .MuiDrawer-paper': closedMixin(sidebarCollapsedWidth)(theme),
                    },
                ]}
                PaperProps={{
                    elevation: 2,
                }}
            >
                <Stack sx={{ height: '100%' }} spacing={2}>
                    {/* Logo Section */}
                    <Stack alignItems="center" justifyContent="center" sx={{ height: '80px', p: '16px' }}>
                        {expanded ? (
                            <Stack alignItems="center" spacing={1}>
                                <img alt="logo" src={authUser?.establishment?.logo?.url ?? logoImg} height="32px" />
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <CloudOffIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                        {t('Offline Mode')}
                                    </Typography>
                                </Stack>
                            </Stack>
                        ) : (
                            <Stack alignItems="center" spacing={1}>
                                <Avatar alt="logo" src={authUser?.establishment?.logo?.url ?? '/favicon.ico'} sx={{ width: 30, height: 30 }} />
                                <CloudOffIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                            </Stack>
                        )}
                    </Stack>

                    {/* Time Remaining Indicator */}
                    {expanded && timeUntilExpiry && timeUntilExpiry > 0 && (
                        <Box sx={{ px: 2 }}>
                            <Chip
                                label={`${t('Expires in')} ${formatTimeRemaining(timeUntilExpiry)}`}
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ width: '100%', fontSize: '0.7rem' }}
                            />
                        </Box>
                    )}

                    {/* Menu Items */}
                    <Box
                        ref={menuItemsBoxWrapperRef}
                        sx={{
                            flex: 1,
                            p: '0 10px',
                            overflowX: 'hidden',
                            overflowY: 'auto',
                            msOverflowStyle: 'none',
                            scrollbarWidth: 'none',
                            '&::-webkit-scrollbar': { display: 'none' },
                        }}
                    >
                        <Stack ref={menuItemsStackWrapperRef} justifyContent="center" spacing={menuItemSpacing}>
                            {offlineSidebarItems.map((menuItem) => {
                                const isMenuItemActive = menuItem.isPathBase ? menuItem.link === pathname : pathname.startsWith(menuItem.link)
                                const Icon = menuItem.icon

                                const button = (
                                    <Button
                                        className={isMenuItemActive ? 'active' : ''}
                                        color="primary"
                                        component={Link}
                                        to={menuItem.link}
                                        fullWidth
                                        startIcon={Icon ? (
                                            <Box sx={{ color: isMenuItemActive ? '#28ACB0' : undefined, fontSize: '24px' }}>
                                                <Icon />
                                            </Box>
                                        ) : undefined}
                                        sx={[
                                            {
                                                color: '#636363',
                                                fontFamily: "'Dongle', sans-serif",
                                                fontSize: '24px',
                                                fontWeight: 400,
                                                letterSpacing: '0.025em',
                                                lineHeight: '18px',
                                                textTransform: 'capitalize',
                                                justifyContent: 'start',
                                                minWidth: 'unset',
                                                pl: '4px',
                                                pr: '4px',
                                                '& .MuiButton-startIcon': {
                                                    width: '32px',
                                                    height: '32px',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    margin: 0,
                                                },
                                            },
                                        ]}
                                    >
                                        <Box component="span" sx={{ position: 'absolute', left: '50px', transform: 'translateY(2px)' }}>
                                            {t(menuItem.label)}
                                        </Box>
                                    </Button>
                                )

                                return expanded ? (
                                    <Box key={menuItem.name}>{button}</Box>
                                ) : (
                                    <Tooltip key={menuItem.name} arrow title={t(menuItem.label)} placement="right">
                                        {button}
                                    </Tooltip>
                                )
                            })}
                        </Stack>
                    </Box>

                    {/* Return to Online Mode */}
                    {expanded && (
                        <Box sx={{ p: 2 }}>
                            <Button
                                component={Link}
                                to="/patients"
                                variant="outlined"
                                fullWidth
                                size="small"
                                sx={{
                                    borderColor: theme.palette.primary.main,
                                    color: theme.palette.primary.main,
                                    fontSize: '0.75rem',
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.main,
                                        color: theme.palette.primary.contrastText,
                                    },
                                }}
                            >
                                {t('Return to Online Mode')}
                            </Button>
                        </Box>
                    )}
                </Stack>
            </Drawer>
        </Box>
    )
} 